// Who holds something, and the picker that changes it: the round badge a person carries, the
// owner chip, and its menu of people with their open counts. The Amazon overview's item
// owners and agent feedback both use it (docs/Y2026-Q4/design-feedback-assignment.md).
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useDismiss } from "../utils/useDismiss";
import styles from "./AssigneePicker.module.css";

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

/** ``person``: their initials; ``nobody``: "+"; ``everyone``: "All"; ``org``: the text given,
 * on the organisation's colour (InRange on the Amazon overview). */
export type MarkTone = "person" | "nobody" | "everyone" | "org";

export const PersonMark = ({
  name,
  tone,
  text,
}: {
  name: string;
  tone: MarkTone;
  text?: string;
}) => (
  <span
    className={`${styles.initials} ${tone === "org" ? styles.org : ""} ${
      tone === "everyone" ? styles.everyone : ""
    }`}
  >
    {text ??
      (tone === "nobody" ? "+" : tone === "everyone" ? "All" : initials(name))}
  </span>
);

export interface PickerOption {
  key: string;
  mark: ReactNode;
  label: ReactNode;
  /** Beside the label, on the right: their open count. */
  aside?: string;
  checked: boolean;
  onPick: () => void;
}

/** Sections of options with a divider between, then Clear when ``onClear`` is given, then
 * a line on what a pick does. */
export const AssigneeMenu = ({
  sections,
  onClear,
  foot,
}: {
  sections: PickerOption[][];
  onClear: (() => void) | null;
  foot: string;
}) => (
  <div className={styles.menu} role="menu">
    {sections
      .filter((s) => s.length > 0)
      .map((section, i) => (
        <Fragment key={i}>
          {i > 0 && <div className={styles.divider} />}
          {section.map((o) => (
            <button
              key={o.key}
              type="button"
              role="menuitemradio"
              aria-checked={o.checked}
              className={styles.option}
              onClick={o.onPick}
            >
              {o.mark}
              <span className={styles.optionText}>{o.label}</span>
              {o.aside && <span className={styles.muted}>{o.aside}</span>}
            </button>
          ))}
        </Fragment>
      ))}
    {onClear && (
      <button
        type="button"
        role="menuitem"
        className={styles.option}
        onClick={onClear}
      >
        <span className={styles.initials}>×</span>
        <span className={styles.optionText}>Clear assignment</span>
      </button>
    )}
    <div className={styles.menuFoot}>{foot}</div>
  </div>
);

// Room a menu of a few people needs beneath its chip; with less, it opens upward.
const MENU_ROOM = 320;

/** The chip naming who holds it ("Assign" when nobody does), opening ``menu`` beneath. */
export const AssigneeChip = ({
  label,
  mark,
  assigned,
  ariaLabel,
  disabled,
  title,
  error,
  menu,
}: {
  label: string;
  mark: ReactNode;
  assigned: boolean;
  ariaLabel: string;
  disabled: boolean;
  title: string | undefined;
  error: string | null;
  menu: (close: () => void) => ReactNode;
}) => {
  // Where the menu hangs: under the chip's right edge, fixed to the window, so a table's
  // scroll box does not clip it. Null while closed.
  const [anchor, setAnchor] = useState<{
    top: number;
    left: number;
    up: boolean;
  } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setAnchor(null), []);
  const open = anchor !== null;
  useDismiss(open, close, ref);
  // A fixed menu would drift from its chip as the page scrolls, so it follows the chip.
  // Closing on scroll instead would also close it when its own list, or the browser
  // bringing an option into view, scrolls.
  useEffect(() => {
    if (!open) return;
    const follow = () => {
      const rect = button.current?.getBoundingClientRect();
      if (!rect) return;
      setAnchor(
        (was) =>
          was && {
            ...was,
            top: was.up ? rect.top : rect.bottom,
            left: rect.right,
          },
      );
    };
    window.addEventListener("scroll", follow, true);
    window.addEventListener("resize", follow);
    return () => {
      window.removeEventListener("scroll", follow, true);
      window.removeEventListener("resize", follow);
    };
  }, [open]);
  const toggle = () => {
    const rect = button.current?.getBoundingClientRect();
    if (open || !rect) return setAnchor(null);
    // It opens where it fits: above the chip when the window has less room below than
    // above.
    const up =
      window.innerHeight - rect.bottom < MENU_ROOM &&
      rect.top > window.innerHeight - rect.bottom;
    setAnchor({ top: up ? rect.top : rect.bottom, left: rect.right, up });
  };
  return (
    <div className={styles.root} ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        ref={button}
        type="button"
        className={`${styles.chip} ${assigned ? "" : styles.unassigned}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={title}
        disabled={disabled}
        onClick={toggle}
      >
        {mark}
        {label}
      </button>
      {error && <div className={styles.error}>{error}</div>}
      {anchor && (
        <div
          className={`${styles.anchor} ${anchor.up ? styles.up : ""}`}
          style={{ top: anchor.top, left: anchor.left }}
        >
          {menu(close)}
        </div>
      )}
    </div>
  );
};
